import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnimatedDemo.css';

// Animated "your holdings are more correlated than you think" walkthrough.
// Same code-driven approach as the other demos (no video).
//
// Built from script 4 in docs/video-scripts.md. The point of the page is
// counterintuitive, so the demo has to make the cluster visible rather than
// describe it: four names light up as one block, then the one that actually
// behaves differently is called out on its own.

const SYMS = ['NVDA', 'AMD', 'AVGO', 'TSM', 'KO'];

// Deliberately realistic: four semis that genuinely trade together, and one
// staple that does not. The numbers are illustrative, not live.
const MATRIX = [
  [1.00, 0.89, 0.86, 0.84, 0.12],
  [0.89, 1.00, 0.82, 0.79, 0.08],
  [0.86, 0.82, 1.00, 0.81, 0.15],
  [0.84, 0.79, 0.81, 1.00, 0.11],
  [0.12, 0.08, 0.15, 0.11, 1.00],
];

// The cluster the demo indicts: everything except KO (index 4).
const CLUSTER = [0, 1, 2, 3];

const cellColor = (v) => {
  if (v >= 0.7) return '#10B981';      // moves together
  if (v >= 0.35) return '#FBBF24';     // loosely related
  return '#EF4444';                    // genuinely different
};

export default function CorrelationsDemo({ autoPlay = true, loop = true }) {
  // 0=grid filling, 1=cluster called out, 2=the diversifier called out
  const [phase, setPhase] = useState(0);
  const [visibleRows, setVisibleRows] = useState(0);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
    timers.current = [];
  };

  const runDemo = useCallback(() => {
    clearTimers();
    setPhase(0);
    setVisibleRows(0);

    // Fill the grid a row at a time so the shape registers before the claim.
    SYMS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisibleRows(i + 1), 400 + i * 320));
    });

    timers.current.push(setTimeout(() => setPhase(1), 2600));
    timers.current.push(setTimeout(() => setPhase(2), 6200));
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return () => clearTimers();
    const loopId = setInterval(runDemo, 11000);
    return () => { clearInterval(loopId); clearTimers(); };
  }, [autoPlay, loop, runDemo]);

  // A cell is ringed when its row AND column are both inside the call-out.
  const highlighted = (r, c) => {
    if (phase === 1) return CLUSTER.includes(r) && CLUSTER.includes(c) && r !== c;
    if (phase === 2) return (r === 4) !== (c === 4); // KO against the others
    return false;
  };

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/correlations</span>
      </div>
      <div className="demo-body">
        <div style={{
          fontSize: '0.68rem', color: 'var(--text-muted, #64748B)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          fontWeight: 700, marginBottom: '0.6rem',
        }}>
          Your holdings, against each other
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: `minmax(38px, 0.7fr) repeat(${SYMS.length}, 1fr)`,
          gap: 3,
        }}>
          {/* Header row */}
          <span />
          {SYMS.map((s) => (
            <span key={`h-${s}`} style={{
              fontFamily: 'var(--font-mono, monospace)', fontSize: '0.58rem',
              fontWeight: 700, color: 'var(--text-secondary, #94A3B8)',
              textAlign: 'center', paddingBottom: 2,
            }}>{s}</span>
          ))}

          {SYMS.map((rowSym, r) => (
            <React.Fragment key={rowSym}>
              <span style={{
                fontFamily: 'var(--font-mono, monospace)', fontSize: '0.58rem',
                fontWeight: 700, color: 'var(--neon-green, #00D4AA)',
                display: 'flex', alignItems: 'center',
                opacity: r < visibleRows ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}>{rowSym}</span>

              {MATRIX[r].map((v, c) => {
                const self = r === c;
                return (
                  <span
                    key={`${rowSym}-${SYMS[c]}`}
                    className="demo-heat-cell"
                    style={{
                      background: self ? 'rgba(148,163,184,0.18)' : cellColor(v),
                      color: self ? 'var(--text-muted, #64748B)' : '#08131F',
                      opacity: r < visibleRows ? 1 : 0,
                      transform: r < visibleRows ? 'scale(1)' : 'scale(0.86)',
                      transition: 'opacity 0.35s ease, transform 0.35s ease, box-shadow 0.35s ease',
                      boxShadow: highlighted(r, c)
                        ? '0 0 0 2px #E2E8F0, 0 0 10px rgba(226,232,240,0.45)'
                        : 'none',
                    }}
                  >
                    {self ? '.' : v.toFixed(2)}
                  </span>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* The read. This is the whole reason the page exists. */}
        <div style={{ minHeight: 58, marginTop: '0.75rem' }}>
          {phase === 1 && (
            <div style={{ animation: 'fadeIn 0.35s ease both' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#10B981', marginBottom: 2 }}>
                These four are effectively one position
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #94A3B8)', lineHeight: 1.5 }}>
                Green means they move together. When one goes, they all go. That is
                a single bet wearing four names, not diversification.
              </div>
            </div>
          )}
          {phase === 2 && (
            <div style={{ animation: 'fadeIn 0.35s ease both' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#EF4444', marginBottom: 2 }}>
                Red is the part worth having
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #94A3B8)', lineHeight: 1.5 }}>
                KO genuinely moves differently from the rest, so it is actually
                doing something for you.
              </div>
            </div>
          )}
        </div>

        <div style={{
          fontSize: '0.64rem', color: 'var(--text-muted, #64748B)',
          borderTop: '1px solid var(--border-subtle, #1E293B)',
          paddingTop: '0.5rem', marginTop: '0.2rem',
        }}>
          Shows your 20 largest by default. Show all is there if you want it.
        </div>
      </div>
    </div>
  );
}
